import { useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import { featureAccess } from "../../permissions/access";
import { IncomeExpenseReportParams } from "../../types/income-expense-report.type";
import { todayInBangkok } from "../../utils/date";
import { useDebounce } from "../../utils/hooks/useDebounce";
import { useSystemSettings } from "../../utils/hooks/useSystemSettings";
import { useUserRole } from "../../utils/hooks/useUserRole";
import { API_DATE_FORMAT, DISPLAY_DATE_FORMAT } from "../DailyExpense/constants";
import SkeletonCard from "../DailyExpense/SkeletonCard";
import StateCard from "../DailyExpense/StateCard";
import { useBranches } from "../SpecialOrder/useBranches";
import IncomeExpenseFilters, { ReportFilterValue } from "./IncomeExpenseFilters";
import IncomeExpenseTable from "./IncomeExpenseTable";
import ReportHeading, { Crumb } from "./ReportHeading";
import ReportLegend from "./ReportLegend";
import RouteDayDrawer from "./RouteDayDrawer";
import { ALL_BRANCHES_TEXT, FILTER_DEBOUNCE_MS } from "./constants";
import {
  Drill,
  ReportLine,
  isRangeTooLong,
  routeLabelOf,
  thresholdsOf,
  viewOf,
} from "./incomeExpenseReport";
import { monthLabelOf } from "./reportFormat";
import { useIncomeExpenseReport } from "./useIncomeExpenseReport";

const TOP_LEVEL: Drill = { branchCode: null, routeUuid: null };

/** The levels that apply, the last one (on screen) not a link. */
const crumbsOf = (candidates: (Crumb | false)[]): Crumb[] => {
  const crumbs = candidates.filter((crumb): crumb is Crumb => crumb !== false);
  return crumbs.map((crumb, index) => (index === crumbs.length - 1 ? { ...crumb, onSelect: null } : crumb));
};

/** This month so far, every branch, ประมาณการ (the prototype's opening state). */
const initialFilter = (): ReportFilterValue => {
  const today = todayInBangkok();
  return { dateFrom: today.startOf("month"), dateTo: today, branchCodes: [], mode: "estimate" };
};

/**
 * S1 รายงานรับจ่าย: profit (loss) per branch → route → day, in estimate or actual mode. One
 * debounced request per settled filter (a newer one aborts it) brings the route-days; drilling
 * and the drawer work on that answer without asking the server again.
 */
export default function IncomeExpenseReportScreen() {
  const role = useUserRole();
  const canPickBranch = featureAccess.incomeExpenseReportAllBranches(role);
  const branches = useBranches(canPickBranch);
  const [filter, setFilter] = useState<ReportFilterValue>(initialFilter);
  const settledFilter = useDebounce(filter, FILTER_DEBOUNCE_MS);
  const params = useMemo<IncomeExpenseReportParams | null>(
    () =>
      isRangeTooLong(settledFilter.dateFrom, settledFilter.dateTo)
        ? null
        : {
            start: settledFilter.dateFrom.format(API_DATE_FORMAT),
            end: settledFilter.dateTo.format(API_DATE_FORMAT),
            mode: settledFilter.mode,
            branchCodes: canPickBranch ? settledFilter.branchCodes : [],
          },
    [settledFilter, canPickBranch],
  );
  const { report, isLoading, isFailed, reload } = useIncomeExpenseReport(params);
  const systemSettings = useSystemSettings();
  const thresholds = useMemo(() => thresholdsOf(systemSettings.settings), [systemSettings.settings]);
  const [drill, setDrill] = useState<Drill>(TOP_LEVEL);
  const [openDate, setOpenDate] = useState<string | null>(null);

  const rows = useMemo(() => report?.rows ?? [], [report]);
  // A branch role reads its own branch only, so it opens on that branch's routes.
  const pinnedBranchCode = canPickBranch ? null : rows[0]?.branch_code ?? null;
  const branchCode = drill.branchCode ?? pinnedBranchCode;
  const routeUuid = branchCode === null ? null : drill.routeUuid;
  const view = useMemo(
    () => (report ? viewOf(rows, report.totals, { branchCode, routeUuid }) : null),
    [report, rows, branchCode, routeUuid],
  );

  const isEstimate = settledFilter.mode === "estimate";
  const showsMissingDocument = !isEstimate;
  const notClosedMonths = report && showsMissingDocument ? report.not_closed_months : [];
  const dayLines = view?.level === "day" ? view.lines : [];
  const openIndex = dayLines.findIndex((line) => line.businessDate === openDate);
  const openLine = openIndex < 0 ? null : dayLines[openIndex];
  const previousDate = dayLines[openIndex - 1]?.businessDate ?? null;
  const nextDate = openIndex < 0 ? null : dayLines[openIndex + 1]?.businessDate ?? null;

  const goTo = (next: Drill) => {
    setOpenDate(null);
    setDrill(next);
  };
  const handleFilterChange = (patch: Partial<ReportFilterValue>) => {
    // Another set of branches is another table: the drill-down starts again from the top.
    if (patch.branchCodes) goTo(TOP_LEVEL);
    setFilter((previous) => ({ ...previous, ...patch }));
  };
  const handleDrill = (line: ReportLine) =>
    goTo({ branchCode: line.branchCode, routeUuid: view?.level === "route" ? line.routeUuid : null });
  const handleOpenDay = (line: ReportLine) => setOpenDate(line.businessDate);
  const showAllBranches = () => goTo(TOP_LEVEL);
  const showBranch = () => goTo({ branchCode, routeUuid: null });
  const closeDrawer = () => setOpenDate(null);
  const showPreviousDay = previousDate ? () => setOpenDate(previousDate) : null;
  const showNextDay = nextDate ? () => setOpenDate(nextDate) : null;

  const branchRow = rows.find((row) => row.branch_code === branchCode);
  const routeRow = rows.find((row) => row.route_uuid === routeUuid);
  const crumbs = crumbsOf([
    canPickBranch && { key: ALL_BRANCHES_TEXT, label: ALL_BRANCHES_TEXT, onSelect: showAllBranches },
    branchCode !== null && { key: branchCode, label: branchCode, onSelect: showBranch },
    routeRow !== undefined && { key: routeRow.route_uuid ?? "", label: routeLabelOf(routeRow), onSelect: null },
  ]);

  const scope = branchRow ? ` สาขา ${branchRow.branch_code} ${branchRow.branch_name}` : "";
  const title = `รายรับ - รายจ่าย${scope} (${isEstimate ? "ข้อมูลประมาณการ" : "ข้อมูลจริง"})`;
  const subtitle = `ประจำวันที่ ${settledFilter.dateFrom.format(DISPLAY_DATE_FORMAT)} - ${settledFilter.dateTo.format(DISPLAY_DATE_FORMAT)}`;

  return (
    <Stack gap={2} sx={{ py: 1 }}>
      <IncomeExpenseFilters
        value={filter}
        canPickBranch={canPickBranch}
        branches={branches}
        isRangeTooLong={isRangeTooLong(filter.dateFrom, filter.dateTo)}
        onChange={handleFilterChange}
      />
      {notClosedMonths.length > 0 && (
        <Alert severity="warning">
          <strong>ยังไม่ปิดเดือน {notClosedMonths.map(monthLabelOf).join(", ")}</strong> –
          แสดงเฉพาะค่าใช้จ่ายรายวันที่อนุมัติแล้ว ยังไม่รวมเงินเดือนและรายการรายเดือนของ HO
        </Alert>
      )}
      <ReportHeading crumbs={crumbs} title={title} subtitle={subtitle} />
      {isLoading && <SkeletonCard />}
      {isFailed && (
        <StateCard
          title="โหลดรายงานไม่สำเร็จ"
          text="เชื่อมต่อเซิร์ฟเวอร์ไม่ได้ ข้อมูลที่กรอกไว้ใน filter ยังอยู่"
          onRetry={reload}
        />
      )}
      {view && view.lines.length === 0 && (
        <StateCard
          title="ไม่พบข้อมูลรับจ่ายในช่วงวันที่ที่เลือก"
          text="ลองเปลี่ยนช่วงวันที่หรือเลือกสาขาเพิ่ม"
        />
      )}
      {view && view.lines.length > 0 && (
        <>
          <IncomeExpenseTable
            view={view}
            thresholds={thresholds}
            isEstimate={isEstimate}
            showsMissingDocument={showsMissingDocument}
            onDrill={handleDrill}
            onOpenDay={handleOpenDay}
          />
          <ReportLegend
            level={view.level}
            thresholds={thresholds}
            isSettingsFailed={systemSettings.hasError}
            showsMissingDocument={showsMissingDocument}
            canOpenSettings={featureAccess.systemSettings(role)}
          />
        </>
      )}
      <RouteDayDrawer
        line={openLine}
        thresholds={thresholds}
        isEstimate={isEstimate}
        showsMissingDocument={showsMissingDocument}
        onPrevious={showPreviousDay}
        onNext={showNextDay}
        onClose={closeDrawer}
      />
    </Stack>
  );
}
